/* The foundation slab.
 *
 * Fixed to the bottom, 84px, with a border on the TOP EDGE ONLY and the deepest
 * shadow in the system thrown upward. The single-edge border is the whole idea:
 * a full-width sticky element with a rule on all four sides stops being the
 * ground the page stands on and becomes a card floating over it, and the moment
 * it reads as a card it stops reading as a transmitter.
 *
 * THREE STATES, AND YOU CAN TELL THEM APART FROM ACROSS A ROOM:
 *
 *   LIVE     the whole slab is the accent. An 84px square play button, the show and
 *            the host, a five-bar level meter that snaps on steps(), the word
 *            LIVE. NO SCRUBBER — a scrubber that cannot scrub is a lie, and
 *            every station site that draws one is lying.
 *   OFF AIR  paper. The accent drains back to the play button alone, and the line
 *            says what is actually playing and when the next show starts. The
 *            state is read by AREA OF COLOUR first and words second.
 *   EPISODE  paper, plus the things on demand actually has: a real waveform
 *            scrubber, −15/+15, elapsed over total, and a route back to live.
 *
 * Colour is never carrying a state on its own. ON AIR has the word LIVE beside
 * the meter; off air says OFF AIR; a DJ who is booked but not yet connected
 * says ON AIR SHORTLY rather than showing an accent slab over a silent mount.
 *
 * Under 760px the whole thing collapses to one 56px row and the detail moves
 * into a full-height sheet, because four cells of information do not fit across
 * a phone and shrinking them until they do is how a player becomes unusable.
 *
 * The controls are real controls: <button> elements with labels, and restyled
 * <input type="range"> for volume and for seeking. A div with a mousedown
 * handler cannot be operated with a keyboard, has no value to announce, and
 * gives a screen reader nothing to say.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import { useChat } from '../chat';
import { LIVE_ARTWORK } from '../brand';
import { Artwork, Chip, OnAirBadge } from '../components';
import {
  formatClock,
  formatDuration,
  formatFullDate,
  formatTime,
  formatTimeRange,
  readTzMode,
} from '../format';
import type { TzMode } from '../format';
import type { ColorToken, GenreRef, HostRef } from '../types';
import { usePlayer } from './PlayerProvider';

/* ---------------------------------------------------------------------- CSS
 *
 * The player's own vocabulary. These are the class names the design canvas uses
 * (.player, .p-play, .p-now, .p-meter, .p-tail, .p-arch); they are here rather
 * than in base.css because base.css is locked and this is the only file that
 * draws them. Everything below is built from the token scales — no radius, no
 * blur, no colour that is not a token.
 *
 * --pl-field / --pl-ink / --pl-rule are the three that make the accent state
 * work. On an accent slab a structural rule CANNOT be --edge: in dark mode
 * --edge is the sky and so is the accent, so that rule would vanish. Inside the
 * accent the structure is ink, which is correct in both modes because the field
 * always takes ink.
 */
const CSS = `
.player {
  --pl-field: var(--paper);
  --pl-ink: var(--text);
  --pl-rule: var(--edge);
  --pl-dim: var(--muted);
  position: fixed; left: 0; right: 0; bottom: 0; z-index: 50;
  height: var(--player-h);
  display: flex; align-items: stretch;
  background: var(--pl-field); color: var(--pl-ink);
  border-top: var(--bw-on) solid var(--edge);
  box-shadow: var(--sh-player);
}
/* LIVE is now a rule and a lit play button, not an 84px accent field.
   The field version was the second-largest area of colour on the site, and it
   said nothing the accent play button and the level meter do not already say. In
   dark mode it also sat a sky fill inside a sky border, which reads as one
   undifferentiated block rather than as a state.
   "Reduce AREA, not saturation" — so the accent moves to a heavy rule along the
   top edge, where it reads as the bar being switched on. */
.player--live {
  --pl-field: var(--surface);
  --pl-ink: var(--text);
  --pl-rule: var(--edge);
  --pl-dim: var(--muted);
  border-top-color: var(--accent);
  border-top-width: 6px;
}
/* With the field gone, base.css's --edge ring is correct again in both modes,
   so no override is needed here. */

.p-play {
  flex: 0 0 var(--player-h); width: var(--player-h);
  background: var(--accent); color: var(--on-accent);
  border-right: var(--bw) solid var(--pl-rule);
  display: flex; align-items: center; justify-content: center;
}
.p-play:hover { background: var(--ink); color: var(--accent); }
.p-glyph { display: flex; align-items: center; justify-content: center;
           transition: transform var(--dur) linear; }
.p-play:active .p-glyph { transform: translate(3px, 3px); }
.p-tri { display: block; width: 0; height: 0; border-style: solid;
         border-width: 17px 0 17px 27px;
         border-color: transparent transparent transparent currentColor; }
.p-bars { gap: 4px; }
.p-bars i { display: block; width: 6px; height: 26px; background: currentColor; }

.p-now { flex: 1 1 auto; min-width: 0; padding: 0 20px;
         display: flex; flex-direction: column; justify-content: center; gap: 5px;
         border-right: var(--bw) solid var(--pl-rule); }
.p-now--fixed { flex: 0 0 320px; width: 320px; }
.pt { font-size: 1.1875rem; line-height: 1; letter-spacing: -.005em;
      font-variation-settings: 'wdth' 96, 'wght' 800; text-transform: uppercase;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pt__w { font-variation-settings: 'wdth' 96, 'wght' 500; }
.p-line { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.p-dim { color: var(--pl-dim); }

/* Three signals, none of them colour alone: a block, a rule and words. */
.p-err { display: flex; align-items: center; gap: 8px; min-width: 0; }
.p-err__blk { flex: 0 0 10px; width: 10px; height: 15px; background: var(--magenta);
              border: var(--bw-chip) solid var(--pl-rule); }
/* Two lines, then stop. An apology cut off mid-word is not an apology. */
.p-err__msg { min-width: 0; line-height: 1.3; overflow: hidden;
              display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }

.p-meter { flex: 0 0 240px; padding: 0 20px; display: flex; align-items: center; gap: 14px;
           border-right: var(--bw) solid var(--pl-rule); }
.p-lv { display: flex; gap: 5px; align-items: flex-end; height: 34px; flex-shrink: 0; }
.p-lv i { display: block; width: 7px; background: currentColor;
          animation-name: pl-lv; animation-iteration-count: infinite;
          animation-timing-function: steps(1, end); }
.p-lv[data-running="false"] i { animation-play-state: paused; }
.p-lv--quiet i { animation-name: pl-lv-quiet; }
.p-lv i:nth-child(1) { height: 22px; animation-duration: 820ms; }
.p-lv i:nth-child(2) { height: 34px; animation-duration: 640ms; animation-delay: -120ms; }
.p-lv i:nth-child(3) { height: 16px; animation-duration: 940ms; animation-delay: -320ms; }
.p-lv i:nth-child(4) { height: 29px; animation-duration: 720ms; animation-delay: -80ms; }
.p-lv i:nth-child(5) { height: 11px; animation-duration: 880ms; animation-delay: -500ms; }
.p-lv--quiet i:nth-child(1) { height: 14px; }
.p-lv--quiet i:nth-child(2) { height: 20px; }
.p-lv--quiet i:nth-child(3) { height: 9px; }
.p-lv--quiet i:nth-child(4) { height: 17px; }
.p-lv--quiet i:nth-child(5) { height: 7px; }
/* A meter snaps like a transmitter lamp. It does not breathe. */
@keyframes pl-lv {
  0% { height: 22px; } 16% { height: 34px; } 32% { height: 11px; }
  48% { height: 29px; } 64% { height: 16px; } 80% { height: 34px; } 100% { height: 20px; }
}
@keyframes pl-lv-quiet {
  0% { height: 12px; } 20% { height: 20px; } 40% { height: 7px; }
  60% { height: 17px; } 80% { height: 9px; } 100% { height: 14px; }
}
@media (prefers-reduced-motion: reduce) { .p-lv i { animation: none; } }

.p-tail { flex: 0 0 172px; padding: 0 20px; display: flex; flex-direction: column;
          justify-content: center; gap: 6px; }
.p-tail--ep { flex: 0 0 214px; }

.p-vol { display: flex; align-items: center; gap: 10px; }
.p-mute { flex: 0 0 auto; min-width: 52px; height: 44px;
          display: inline-flex; align-items: center; justify-content: center;
          border: var(--bw-chip) solid var(--pl-rule); background: transparent; color: inherit; }
.p-mute:hover { background: var(--ink); color: var(--accent); }
.p-mute[aria-pressed="true"] { text-decoration: line-through; text-decoration-thickness: 3px;
                               box-shadow: inset 0 0 0 var(--bw-chip) var(--pl-rule); }

.p-range { -webkit-appearance: none; appearance: none; display: block;
           width: 100%; height: 44px; margin: 0; padding: 0;
           background: transparent; color: inherit; cursor: pointer; }
.p-range::-webkit-slider-runnable-track {
  height: 20px; border: var(--bw-chip) solid var(--pl-rule); background-color: transparent;
  background-image: repeating-linear-gradient(90deg, transparent 0 13px, var(--pl-field) 13px 16px),
                    linear-gradient(var(--pl-rule), var(--pl-rule));
  background-repeat: no-repeat, no-repeat;
  background-size: 100% 100%, var(--p-fill, 0%) 100%;
  background-position: left center, left center;
}
.p-range::-webkit-slider-thumb { -webkit-appearance: none; appearance: none;
  width: 8px; height: 32px; margin-top: -6px; background: var(--pl-field);
  border: var(--bw-chip) solid var(--pl-rule); }
.p-range::-moz-range-track { height: 20px; border: var(--bw-chip) solid var(--pl-rule);
  background-color: transparent;
  background-image: repeating-linear-gradient(90deg, transparent 0 13px, var(--pl-field) 13px 16px); }
.p-range::-moz-range-progress { height: 16px; background: var(--pl-rule); }
.p-range::-moz-range-thumb { width: 8px; height: 32px; border-radius: 0;
  background: var(--pl-field); border: var(--bw-chip) solid var(--pl-rule); }

.p-arch { flex: 1 1 auto; min-width: 0; padding: 0 20px;
          display: flex; align-items: center; gap: 14px;
          border-right: var(--bw) solid var(--pl-rule); }
.p-schedule { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.p-skip { flex: 0 0 auto; min-width: 48px; height: 44px;
          display: inline-flex; align-items: center; justify-content: center;
          border: var(--bw-chip) solid var(--pl-rule); background: transparent; color: inherit;
          transition: transform var(--dur) linear; }
.p-skip:hover { background: var(--ink); color: var(--accent); }
.p-skip:active { transform: translate(3px, 3px); }

.p-scrub { position: relative; flex: 1 1 auto; min-width: 60px; height: 44px; overflow: hidden; }
.p-scrub .wave { position: absolute; left: 0; right: 0; top: 4px; height: 36px; }
.p-seek { position: absolute; inset: 0; width: 100%; height: 100%;
          -webkit-appearance: none; appearance: none; background: transparent;
          margin: 0; padding: 0; cursor: pointer; }
.p-seek::-webkit-slider-runnable-track { height: 44px; background: transparent; border: 0; }
.p-seek::-webkit-slider-thumb { -webkit-appearance: none; appearance: none;
  width: 4px; height: 44px; margin-top: 0; background: var(--pl-rule); border: 0; }
.p-seek::-moz-range-track { height: 44px; background: transparent; border: 0; }
.p-seek::-moz-range-progress { height: 0; background: transparent; }
.p-seek::-moz-range-thumb { width: 4px; height: 44px; border: 0; border-radius: 0;
  background: var(--pl-rule); }

.p-link { display: inline-flex; align-items: center; min-height: 26px;
          text-decoration: underline; text-underline-offset: 3px; color: inherit; }
.p-link:hover { background: var(--ink); color: var(--accent); text-decoration: none; }
.p-source { color: inherit; text-decoration: underline; text-underline-offset: 3px; }
.p-source:hover { background: var(--ink); color: var(--accent); text-decoration: none; }
.p-sc-credit { display: inline-flex; align-items: center; gap: 7px; max-width: 100%; }
.p-sc-credit__mark { flex: 0 0 auto; box-sizing: content-box; width: 40px; height: 10px;
                     object-fit: contain; padding: 5px; background: #121212; }
.p-sc-credit__name { min-width: 0; overflow: hidden; text-overflow: ellipsis;
                     white-space: nowrap; }
.p-close { flex: 0 0 56px; width: 56px; border-left: var(--bw) solid var(--pl-rule);
           display: flex; align-items: center; justify-content: center;
           font-size: 1.5rem; line-height: 1; color: inherit; }
.p-close:hover { background: var(--ink); color: var(--accent); }

/* Chat is one permanent transmitter cell, not a floating badge. The room may
   be off air, but its door stays in the same place on every page. The count is
   messages missed while the panel was shut; it never pretends to be a people
   count. */
.p-chat { flex: 0 0 var(--player-h); width: var(--player-h);
          border-left: var(--bw) solid var(--pl-rule);
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 5px; color: inherit; text-align: center; }
.p-chat:hover, .p-chat[aria-expanded="true"] { background: var(--ink); color: var(--mark); }
.p-chat__count { min-width: 24px; min-height: 20px; padding: 2px 5px;
                 border: var(--bw-chip) solid currentColor;
                 display: inline-flex; align-items: center; justify-content: center; }

/* The ladder down to the phone. Cells leave in order of how little they carry:
   the meter bars first (the WORD stays, it is the actual signal), then volume.
   The show and the play button never leave. */
@media (max-width: 1400px) { .p-now--status { flex: 0 0 auto; padding: 0 14px; }
                             .p-now--status .p-line { display: none; } }
@media (max-width: 1100px) { .p-lv { display: none; } .p-meter { flex: 0 0 120px; } }
@media (max-width: 980px) { .p-now--fixed { flex: 1 1 0; width: auto; }
                            .p-now--status { display: none; } }
@media (max-width: 860px) { .p-tail { display: none; } }

/* ------------------------------- the collapsed row ------------------------ */
.player--compact { height: var(--player-h); }
.p-play--sm { flex: 0 0 56px; width: 56px; }
.p-play--sm .p-tri { border-width: 11px 0 11px 17px; }
.p-play--sm .p-bars { gap: 3px; }
.p-play--sm .p-bars i { width: 4px; height: 18px; }
.p-open { flex: 1 1 auto; min-width: 0; padding: 0 12px;
          display: flex; flex-direction: column; justify-content: center; gap: 2px;
          color: inherit; }
.p-open:hover { background: var(--ink); color: var(--accent); }
.p-mt { font-size: .875rem; line-height: 1; font-variation-settings: 'wdth' 92, 'wght' 800;
        text-transform: uppercase; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.p-mm { font-size: .625rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.p-mcell { flex: 0 0 52px; width: 52px; border-left: var(--bw) solid var(--pl-rule);
           display: flex; align-items: center; justify-content: center; }
.p-sc-cell { flex: 0 0 62px; width: 62px; border-left: var(--bw) solid var(--pl-rule);
             display: inline-flex; flex-direction: column; align-items: center;
             justify-content: center; gap: 3px; color: inherit; text-decoration: none; }
.p-sc-cell:hover { background: var(--ink); color: var(--accent); }
.p-sc-cell .p-sc-credit__mark { width: 32px; height: 8px; padding: 3px; }
.p-sc-cell .p-sc-credit__name { font: 500 .5625rem/1 var(--ff-mono); }
.p-mcell .p-lv { height: 22px; gap: 3px; }
.p-mcell .p-lv i { width: 4px; }
.p-mcell .p-lv i:nth-child(1) { height: 13px; }
.p-mcell .p-lv i:nth-child(2) { height: 22px; }
.p-mcell .p-lv i:nth-child(3) { height: 9px; }
.p-mcell .p-lv i:nth-child(4) { height: 18px; }
.p-mcell .p-lv i:nth-child(5) { display: none; }
.p-mclock { font-size: .625rem; letter-spacing: .04em; }
.p-chat--compact { flex: 0 0 56px; width: 56px; gap: 2px; }
.p-chat--compact .p-chat__count { min-width: 20px; min-height: 17px; padding: 0 3px; }

/* ------------------------------------ the sheet --------------------------- */
.p-sheet { position: fixed; inset: 0; z-index: 80; background: var(--paper); color: var(--text);
           display: flex; flex-direction: column; overflow-y: auto; }
.p-sheet__bar { position: sticky; top: 0; z-index: 1; flex-shrink: 0;
                height: 56px; display: flex; align-items: center; gap: 12px;
                padding-left: 12px; background: var(--paper);
                border-bottom: var(--bw) solid var(--edge); }
.p-sheet__x { margin-left: auto; flex: 0 0 56px; width: 56px; height: 56px;
              display: flex; align-items: center; justify-content: center;
              border-left: var(--bw) solid var(--edge); font-size: 1.5rem; line-height: 1;
              color: inherit; }
.p-sheet__x:hover { background: var(--ink); color: var(--accent); }
.p-sheet__body { padding: 16px 12px 32px; display: flex; flex-direction: column; gap: 16px; }
.p-sheet .art { width: 100%; max-width: 300px; }
.p-sheet__title { font-size: 1.75rem; line-height: .96; letter-spacing: -.01em;
                  font-variation-settings: 'wdth' 96, 'wght' 800; text-transform: uppercase;
                  margin: 0; }
.p-sheet__hosts { display: flex; flex-wrap: wrap; gap: 12px; }
.p-sheet__row { display: flex; align-items: center; gap: 12px; }
.p-sheet__row .p-scrub { flex: 1 1 auto; }
.p-sheet .btn-lg { width: 100%; }
`;

/* ------------------------------------------------------------------ helpers */

const COMPACT_QUERY = '(max-width: 760px)';

function subscribeCompact(onChange: () => void): () => void {
  const query = window.matchMedia(COMPACT_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

function readCompact(): boolean {
  return window.matchMedia(COMPACT_QUERY).matches;
}

/** True below 760px, where the bar collapses to one row. */
function useCompact(): boolean {
  return useSyncExternalStore(subscribeCompact, readCompact, () => false);
}

const SOUNDCLOUD_MARK = '/brand/soundcloud-logo-white.png';

/** Official SoundCloud mark, uploader credit and canonical work permalink. */
function SoundCloudCredit({ url, className = 'p-source' }: { url: string; className?: string }) {
  return (
    <a
      className={`${className} p-sc-credit`}
      href={url}
      target="_blank"
      rel="noreferrer"
      aria-label="HGR (Higher Ground Radio) on SoundCloud — open this track"
    >
      <img className="p-sc-credit__mark" src={SOUNDCLOUD_MARK} alt="SoundCloud" />
      <span className="p-sc-credit__name">HGR ↗</span>
    </a>
  );
}

function ChatLauncher({ compact = false }: { compact?: boolean }) {
  const { available, closeChat, open, openChat, panelId, unread } = useChat();

  const shownUnread = unread > 99 ? '99+' : String(unread);
  const label = open
    ? 'Close chat'
    : unread > 0
      ? `Open chat, ${unread} unread ${unread === 1 ? 'message' : 'messages'}`
      : available
        ? 'Open station chat'
        : 'Open chat';

  return (
    <button
      type="button"
      className={compact ? 'p-chat p-chat--compact' : 'p-chat'}
      onClick={open ? closeChat : openChat}
      aria-label={label}
      aria-controls={panelId}
      aria-expanded={open}
    >
      <span className="mono">Chat</span>
      {unread > 0 ? <span className="mono p-chat__count">{shownUnread}</span> : null}
    </button>
  );
}

/*
 * The visitor's time-zone choice lives in localStorage and is written by a
 * control that is not in this file. `storage` only fires in OTHER tabs, so the
 * subscription also listens for focus and takes a slow poll — the player shows
 * exactly one clock time (the next show) and it should not be the last thing on
 * the page still displaying the old zone.
 */
function subscribeTz(onChange: () => void): () => void {
  window.addEventListener('storage', onChange);
  window.addEventListener('focus', onChange);
  document.addEventListener('visibilitychange', onChange);
  const id = window.setInterval(onChange, 3_000);
  return () => {
    window.removeEventListener('storage', onChange);
    window.removeEventListener('focus', onChange);
    document.removeEventListener('visibilitychange', onChange);
    window.clearInterval(id);
  };
}

function serverTz(): TzMode {
  return 'station';
}

function useTzMode(): TzMode {
  return useSyncExternalStore(subscribeTz, readTzMode, serverTz);
}

function names(hosts: HostRef[]): string {
  if (hosts.length === 0) return '';
  if (hosts.length === 1) return hosts[0].name;
  return `${hosts.slice(0, -1).map((host) => host.name).join(', ')} & ${hosts[hosts.length - 1].name}`;
}

function slotNames(title: string, hosts: HostRef[]): string {
  return /\bw\/\s*\S/i.test(title) ? '' : names(hosts);
}

function genreLine(genres: GenreRef[]): string {
  return genres.map((genre) => genre.name).join(' / ');
}

function clamp(value: number, low: number, high: number): number {
  if (!Number.isFinite(value)) return low;
  return Math.min(high, Math.max(low, value));
}

/* --------------------------------------------------------------- the peaks */

/**
 * audiowaveform v2 JSON, which is what api/hgr/audio.py writes: `data` is
 * interleaved [min, max, min, max, …] at 8 or 16 bits. Reduced to one 0..1
 * amplitude per bucket.
 */
function decodePeaks(payload: unknown): number[] | null {
  const source = payload as { data?: unknown; bits?: unknown } | null;
  const raw = Array.isArray(payload) ? payload : source?.data;
  if (!Array.isArray(raw) || raw.length < 2) return null;

  const bits = typeof source?.bits === 'number' ? source.bits : 8;
  const ceiling = bits === 16 ? 32_768 : 128;

  const values: number[] = [];
  for (let index = 0; index + 1 < raw.length; index += 2) {
    const low = Number(raw[index]);
    const high = Number(raw[index + 1]);
    if (!Number.isFinite(low) || !Number.isFinite(high)) continue;
    values.push(Math.min(1, Math.max(Math.abs(low), Math.abs(high)) / ceiling));
  }
  return values.length > 0 ? values : null;
}

/** Peak-preserving downsample. A mean would flatten every transient into mush. */
function resample(values: number[], count: number): number[] {
  const out: number[] = [];
  if (count <= 0 || values.length === 0) return out;
  const span = values.length / count;
  for (let index = 0; index < count; index += 1) {
    const start = Math.floor(index * span);
    const end = Math.min(values.length, Math.max(start + 1, Math.floor((index + 1) * span)));
    let peak = 0;
    for (let cursor = start; cursor < end; cursor += 1) {
      if (values[cursor] > peak) peak = values[cursor];
    }
    out.push(peak);
  }
  return out;
}

/** Loads the peaks for one episode, or gives up quietly. */
function usePeaks(url: string | null): number[] | null {
  const [peaks, setPeaks] = useState<number[] | null>(null);

  useEffect(() => {
    setPeaks(null);
    if (!url) return;

    const controller = new AbortController();
    fetch(url, { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('no peaks'))))
      .then((body: unknown) => {
        const values = decodePeaks(body);
        if (values) setPeaks(values);
      })
      .catch(() => {
        // The peaks file is a picture, not the audio. If it will not load we
        // draw an even field rather than invent a shape we cannot vouch for.
      });

    return () => controller.abort();
  }, [url]);

  return peaks;
}

/* ------------------------------------------------------------------ glyphs */

function PlayGlyph({ playing }: { playing: boolean }) {
  if (playing) {
    return (
      <span className="p-glyph p-bars" aria-hidden="true">
        <i />
        <i />
      </span>
    );
  }
  return <span className="p-glyph p-tri" aria-hidden="true" />;
}

function LevelMeter({ running, quiet }: { running: boolean; quiet: boolean }) {
  return (
    <div className={quiet ? 'p-lv p-lv--quiet' : 'p-lv'} data-running={running} aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
      <i />
    </div>
  );
}

/* ---------------------------------------------------------------- scrubber */

const BAR_WIDTH = 3;
const BAR_GAP = 2;
const BAR_HEIGHT = 36;
/** The height an unknown waveform draws at. Even, so it claims nothing. */
const FLAT = 0.36;

interface ScrubberProps {
  peaks: number[] | null;
  position: number;
  duration: number;
  onSeek: (seconds: number) => void;
  label: string;
}

function Scrubber({ peaks, position, duration, onSeek, label }: ScrubberProps) {
  const [width, setWidth] = useState(0);
  /* While the handle is held, the bar follows the finger but the audio does
     not: seeking on every input event fires a byte-range request per pixel. */
  const [draft, setDraft] = useState<number | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const setNode = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;
    setWidth(node.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width;
      if (typeof measured === 'number') setWidth(measured);
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  const count = Math.max(8, Math.min(320, Math.floor((width + BAR_GAP) / (BAR_WIDTH + BAR_GAP))));

  const bars = useMemo(
    () => (peaks ? resample(peaks, count) : new Array<number>(count).fill(FLAT)),
    [count, peaks],
  );

  const shown = draft ?? position;
  const max = Math.max(1, Math.round(duration));
  const value = clamp(Math.round(shown), 0, max);
  const played = duration > 0 ? Math.round((value / duration) * count) : 0;

  const commit = () => {
    if (draft === null) return;
    onSeek(draft);
    setDraft(null);
  };

  return (
    <div className="p-scrub">
      <div className="wave" ref={setNode} aria-hidden="true">
        {bars.map((amplitude, index) => (
          <span
            key={index}
            className={index < played ? 'wave__bar wave__bar--played' : 'wave__bar'}
            style={{ height: `${Math.max(3, Math.round(amplitude * BAR_HEIGHT))}px` }}
          />
        ))}
      </div>
      <input
        className="p-seek"
        type="range"
        min={0}
        max={max}
        step={1}
        value={value}
        aria-label={label}
        aria-valuetext={`${formatClock(value)} of ${formatClock(duration)}`}
        onChange={(event) => setDraft(Number(event.currentTarget.value))}
        onPointerUp={commit}
        onPointerCancel={commit}
        onKeyUp={commit}
        onBlur={commit}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ volume */

function VolumeControl() {
  const { volume, muted, setVolume, toggleMute } = usePlayer();
  const percent = Math.round(volume * 100);

  return (
    <div className="p-vol">
      <button
        type="button"
        className="p-mute mono"
        onClick={toggleMute}
        aria-pressed={muted}
        aria-label={muted ? 'Unmute the player' : 'Mute the player'}
      >
        {muted ? 'Muted' : 'Vol'}
      </button>
      <input
        className="p-range"
        type="range"
        min={0}
        max={100}
        step={1}
        value={percent}
        aria-label="Volume"
        aria-valuetext={`${percent}%`}
        style={{ '--p-fill': `${percent}%` } as CSSProperties}
        onChange={(event) => setVolume(Number(event.currentTarget.value) / 100)}
      />
    </div>
  );
}

/* -------------------------------------------------------- what to say where */

interface Readout {
  /** true when the whole slab goes accent. */
  onAir: boolean;
  /** The big line. */
  title: string;
  /** The lighter half of the big line — hosts, or what the schedule is playing. */
  suffix: string;
  /** The mono line under it. */
  meta: string;
  /** The word beside the level meter. Never colour on its own. */
  word: string;
  /** Whether the meter should be running. */
  running: boolean;
  /** The quiet meter is the 24/7 loop, not a live desk. */
  quiet: boolean;
  playLabel: string;
  /** Title and host on one line, for the 56px row. */
  compactLine: string;
  /** One status fact and the tap hint. Sized to fit a 360px screen. */
  compactMeta: string;
}

function useReadout(tz: TzMode): Readout {
  const {
    live,
    mode,
    source,
    current,
    isPlaying,
    isLoading,
    soundCloudReady,
    isStationSchedule,
    stationSchedule,
    position,
  } = usePlayer();

  if (mode === 'episode' && current) {
    const waitingForSoundCloudPress =
      source === 'soundcloud' && soundCloudReady && !isPlaying && !isLoading;
    return {
      onAir: false,
      title: current.title,
      suffix: '',
      meta: waitingForSoundCloudPress
        ? 'SoundCloud ready — press play again'
        : [
            isStationSchedule ? 'Scheduled set' : '',
            names(current.hosts) || 'Higher Ground Radio',
            current.catalogue_no,
            isStationSchedule && stationSchedule
              ? `until ${formatTime(stationSchedule.ends_at, tz)}`
              : '',
          ].filter(Boolean).join(' · '),
      word: isLoading
        ? 'Loading'
        : waitingForSoundCloudPress
          ? 'Ready'
          : isStationSchedule
            ? 'Schedule'
            : 'Archive',
      running: isPlaying,
      quiet: true,
      playLabel: `${isPlaying ? 'Pause' : 'Play'} ${current.catalogue_no}, ${current.title}`,
      compactLine: [current.title, names(current.hosts)].filter(Boolean).join(' · '),
      compactMeta: waitingForSoundCloudPress
        ? 'SoundCloud ready · press play'
        : isStationSchedule && stationSchedule
          ? `Scheduled until ${formatTime(stationSchedule.ends_at, tz)}${
              stationSchedule.next[0]
                ? ` · Next ${stationSchedule.next[0].title}`
                : ''
            }`
          : `${source === 'soundcloud' ? 'SoundCloud · ' : ''}${current.catalogue_no} · Tap for detail`,
    };
  }

  if (!live) {
    return {
      onAir: false,
      title: 'Higher Ground Radio',
      suffix: '',
      meta: 'Reaching the station',
      word: 'Wait',
      running: false,
      quiet: true,
      playLabel: 'Play the station',
      compactLine: 'Higher Ground Radio',
      compactMeta: 'Reaching the station',
    };
  }

  if (live.live && live.now) {
    const now = live.now;
    const status = now.dj_connected ? `Live ${formatClock(position)}` : 'On air shortly';
    const genres = genreLine(now.genres);
    const hosts = slotNames(now.title, now.hosts);
    return {
      onAir: true,
      title: now.title,
      suffix: hosts ? `w/ ${hosts}` : '',
      meta: [now.catalogue_no, status, genres].filter(Boolean).join(' · '),
      word: isLoading ? 'Connecting' : now.dj_connected ? 'Live' : 'Standby',
      running: isPlaying && now.dj_connected,
      quiet: false,
      playLabel: isPlaying
        ? `Stop the live stream, ${now.title}`
        : `Play the live stream, ${now.title}`,
      compactLine: [now.title, hosts].filter(Boolean).join(' · '),
      compactMeta: `${status} · Tap for detail`,
    };
  }

  // Off air. D7: the stream never actually stops, so this says what is playing
  // rather than pretending there is nothing to press.
  const fallback = live.fallback;
  const label = fallback?.label ?? 'Higher Ground 24/7';
  const playing = fallback?.playing ?? null;
  const upcoming = live.next[0] ?? null;

  const descriptor = playing
    ? `· ${playing.catalogue_no} ${playing.title}`
    : fallback?.kind === 'silence'
      ? '· silence'
      : '· archive shuffle';

  const nextPart = upcoming
    ? `Next ${formatTime(upcoming.starts_at, tz)} · ${upcoming.title}${
        slotNames(upcoming.title, upcoming.hosts)
          ? ` w/ ${slotNames(upcoming.title, upcoming.hosts)}`
          : ''
      }`
    : 'Nothing booked yet';

  return {
    onAir: false,
    title: label,
    suffix: descriptor,
    meta: `Off air · ${nextPart}`,
    word: isLoading ? 'Connecting' : fallback?.kind === 'silence' ? 'Silent' : '24/7',
    running: isPlaying,
    quiet: true,
    playLabel: isPlaying ? `Stop ${label}` : `Play ${label}`,
    compactLine: [label, playing?.title].filter(Boolean).join(' · '),
    compactMeta: upcoming
      ? `Off air · Next ${formatTime(upcoming.starts_at, tz)} · Tap for detail`
      : 'Off air · Tap for detail',
  };
}

/* ------------------------------------------------------------ the sheet */

interface SheetProps {
  onClose: () => void;
  tz: TzMode;
  peaks: number[] | null;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

function Sheet({ onClose, tz, peaks }: SheetProps) {
  const {
    live,
    mode,
    source,
    current,
    isPlaying,
    isLoading,
    isStationSchedule,
    stationSchedule,
    error,
    position,
    duration,
    toggle,
    seek,
    skip,
    backToLive,
    close,
  } = usePlayer();

  const sheetRef = useRef<HTMLDivElement | null>(null);
  const readout = useReadout(tz);

  useEffect(() => {
    const node = sheetRef.current;
    if (!node) return;

    const restoreTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const target = node.querySelector<HTMLElement>('[data-autofocus]');
    (target ?? node).focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey, true);
    const restoreOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = restoreOverflow;
      restoreTo?.focus();
    };
  }, [onClose]);

  const episode = mode === 'episode' ? current : null;
  const now = live?.now ?? null;

  const colour: ColorToken = episode
    ? episode.artwork_color
    : (now?.artwork_color ?? 'ink');
  const catalogue = episode
    ? episode.catalogue_no
    : (now?.catalogue_no ?? live?.fallback?.playing?.catalogue_no ?? 'HGR');
  const hosts: HostRef[] = episode ? episode.hosts : (now?.hosts ?? live?.fallback?.playing?.hosts ?? []);
  const genres: GenreRef[] = episode ? episode.genres : (now?.genres ?? []);

  return (
    <div
      className="p-sheet"
      role="dialog"
      aria-modal="true"
      aria-label="The player"
      tabIndex={-1}
      ref={sheetRef}
    >
      <div className="p-sheet__bar">
        <span className="mono">Now playing</span>
        <button
          type="button"
          className="p-sheet__x"
          onClick={onClose}
          aria-label="Close this panel"
          data-autofocus
        >
          ×
        </button>
      </div>

      <div className="p-sheet__body">
        <Artwork
          color={colour}
          imageUrl={episode?.artwork_url ?? (now ? LIVE_ARTWORK : undefined)}
          catalogueNo={catalogue}
          size="hero"
        />

        {episode ? (
          <span className="badge">{isStationSchedule ? 'Scheduled set' : 'Archive'}</span>
        ) : (
          <OnAirBadge
            live={Boolean(live?.live)}
            label={live?.live && now?.dj_connected === false ? 'ON AIR SHORTLY' : undefined}
          />
        )}

        <h2 className="p-sheet__title">{readout.title}</h2>

        {hosts.length > 0 && (
          <div className="p-sheet__hosts">
            {hosts.map((host) => (
              <Link key={host.slug} className="mono-13" to={`/residents/${host.slug}`}>
                {host.name}
              </Link>
            ))}
          </div>
        )}

        {genres.length > 0 && (
          <div className="chips">
            {genres.map((genre) => (
              <Chip key={genre.slug} color={genre.color_token} to={`/genre/${genre.slug}`}>
                {genre.name}
              </Chip>
            ))}
          </div>
        )}

        <div className="mono dim">
          {episode
            ? [
                episode.catalogue_no,
                formatFullDate(episode.broadcast_at, tz),
                formatDuration(episode.duration_seconds),
                episode.location_short,
              ]
                .filter(Boolean)
                .join(' · ')
            : now
              ? [now.catalogue_no, formatTimeRange(now.starts_at, now.ends_at, tz), now.location_short]
                  .filter(Boolean)
                  .join(' · ')
              : readout.meta}
        </div>

        {episode && isStationSchedule && stationSchedule ? (
          <div className="mono-13" aria-label="SoundCloud schedule">
            On now until {formatTime(stationSchedule.ends_at, tz)}
            {stationSchedule.next.length > 0
              ? ` · Next: ${stationSchedule.next.map((item) => item.title).join(' · ')}`
              : ''}
          </div>
        ) : null}

        {episode && source === 'soundcloud' && episode.soundcloud_url ? (
          <SoundCloudCredit url={episode.soundcloud_url} className="btn-ghost" />
        ) : null}

        {error && (
          <div className="p-err" role="status">
            <span className="p-err__blk" aria-hidden="true" />
            <span className="mono">{error}</span>
          </div>
        )}

        {episode && !isStationSchedule ? (
          <>
            <div className="p-sheet__row">
              <button
                type="button"
                className="p-skip mono"
                onClick={() => skip(-15)}
                aria-label="Back fifteen seconds"
              >
                −15
              </button>
              <Scrubber
                peaks={peaks}
                position={position}
                duration={duration}
                onSeek={seek}
                label={`Seek within ${episode.catalogue_no}`}
              />
              <button
                type="button"
                className="p-skip mono"
                onClick={() => skip(15)}
                aria-label="Forward fifteen seconds"
              >
                +15
              </button>
            </div>
            <div className="mono-13">
              {formatClock(position)} / {formatClock(duration)}
            </div>
          </>
        ) : episode && stationSchedule ? (
          <div className="mono-13">
            Shared programme · {formatTime(stationSchedule.starts_at, tz)}–
            {formatTime(stationSchedule.ends_at, tz)}
          </div>
        ) : (
          <div className="mono-13">
            {live?.live && now ? `Live ${formatClock(position)}` : readout.word}
          </div>
        )}

        <button type="button" className="btn-lg" onClick={toggle}>
          <PlayGlyph playing={isPlaying || isLoading} />
          {isPlaying ? 'Pause' : isLoading ? 'Connecting' : 'Play'}
        </button>

        <VolumeControl />

        {episode && (
          <>
            <Link className="btn-ghost" to={`/episodes/${episode.catalogue_no}`} onClick={onClose}>
              Open the episode
            </Link>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                backToLive();
                onClose();
              }}
            >
              ← Back to live
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                close();
                onClose();
              }}
            >
              Close the archive player
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- the bar */

export function PlayerBar() {
  const {
    live,
    mode,
    source,
    current,
    isPlaying,
    isLoading,
    isStationSchedule,
    stationSchedule,
    error,
    position,
    duration,
    toggle,
    seek,
    skip,
    backToLive,
    close,
  } = usePlayer();

  const tz = useTzMode();
  const compact = useCompact();
  const readout = useReadout(tz);
  const peaks = usePeaks(mode === 'episode' ? (current?.waveform_url ?? null) : null);

  const [sheetOpen, setSheetOpen] = useState(false);
  const closeSheet = useCallback(() => setSheetOpen(false), []);

  // The sheet belongs to the phone. Rotating a tablet into landscape while it
  // is open should put the bar back, not leave a modal with no way out.
  useEffect(() => {
    if (!compact) setSheetOpen(false);
  }, [compact]);

  const episode = mode === 'episode' ? current : null;
  const slabClass = readout.onAir ? 'player player--live' : 'player';

  /* ------------------------------------------------------------- compact */

  if (compact) {
    return (
      <>
        <style>{CSS}</style>
        <div className={`${slabClass} player--compact`}>
          <button
            type="button"
            className="p-play p-play--sm"
            onClick={toggle}
            aria-label={readout.playLabel}
          >
            <PlayGlyph playing={isPlaying || isLoading} />
          </button>

          <button
            type="button"
            className="p-open"
            onClick={() => setSheetOpen(true)}
            aria-expanded={sheetOpen}
            aria-haspopup="dialog"
          >
            <span className="p-mt" aria-live="polite">
              {readout.compactLine}
            </span>
            <span className="mono p-mm">{error ?? readout.compactMeta}</span>
          </button>

          {episode && source === 'soundcloud' && episode.soundcloud_url ? (
            <SoundCloudCredit url={episode.soundcloud_url} className="p-sc-cell" />
          ) : null}

          <div className="p-mcell">
            {episode ? (
              <span className="mono p-mclock">{formatClock(position)}</span>
            ) : (
              <LevelMeter running={readout.running} quiet={readout.quiet} />
            )}
          </div>

          <ChatLauncher compact />
        </div>

        {sheetOpen && <Sheet onClose={closeSheet} tz={tz} peaks={peaks} />}
      </>
    );
  }

  /* --------------------------------------------------------- episode bar */

  if (episode) {
    const stationLine = live?.live && live.now
      ? `Live now: ${live.now.title}${
          slotNames(live.now.title, live.now.hosts)
            ? ` w/ ${slotNames(live.now.title, live.now.hosts)}`
            : ''
        }`
      : live?.next[0]
        ? `Next on air ${formatTime(live.next[0].starts_at, tz)} · ${live.next[0].title}`
        : 'Off air · Higher Ground 24/7';

    return (
      <>
        <style>{CSS}</style>
        <div className={slabClass}>
          <button type="button" className="p-play" onClick={toggle} aria-label={readout.playLabel}>
            <PlayGlyph playing={isPlaying || isLoading} />
          </button>

          <div className="p-now p-now--fixed">
            <div className="pt" aria-live="polite">
              {episode.title}
            </div>
            {error ? (
              <div className="p-err" role="status">
                <span className="p-err__blk" aria-hidden="true" />
                <span className="mono p-err__msg">{error}</span>
              </div>
            ) : (
              <div className="mono p-line p-dim">{readout.meta}</div>
            )}
          </div>

          <div className="p-arch">
            {isStationSchedule && stationSchedule ? (
              <div className="mono p-schedule">
                Shared programme {formatTime(stationSchedule.starts_at, tz)}–
                {formatTime(stationSchedule.ends_at, tz)}
                {stationSchedule.next[0] ? ` · Next ${stationSchedule.next[0].title}` : ''}
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className="p-skip mono"
                  onClick={() => skip(-15)}
                  aria-label="Back fifteen seconds"
                >
                  −15
                </button>
                <Scrubber
                  peaks={peaks}
                  position={position}
                  duration={duration}
                  onSeek={seek}
                  label={`Seek within ${episode.catalogue_no}`}
                />
                <button
                  type="button"
                  className="p-skip mono"
                  onClick={() => skip(15)}
                  aria-label="Forward fifteen seconds"
                >
                  +15
                </button>
              </>
            )}
          </div>

          <div className="p-now p-now--status">
            <div className="mono-13">
              {formatClock(position)} / {formatClock(duration)}
            </div>
            <div className="mono p-line p-dim">
              {isLoading ? 'Buffering' : stationLine}
            </div>
          </div>

          {source === 'soundcloud' && episode.soundcloud_url ? (
            <SoundCloudCredit url={episode.soundcloud_url} className="p-sc-cell" />
          ) : null}

          <div className="p-tail p-tail--ep">
            <button type="button" className="p-link mono" onClick={backToLive}>
              ← Back to live
            </button>
            <VolumeControl />
          </div>

          <ChatLauncher />

          <button
            type="button"
            className="p-close"
            onClick={close}
            aria-label="Close the archive player and stop the audio"
          >
            ×
          </button>
        </div>
      </>
    );
  }

  /* ------------------------------------------------------- live / off air */

  return (
    <>
      <style>{CSS}</style>
      <div className={slabClass}>
        <button type="button" className="p-play" onClick={toggle} aria-label={readout.playLabel}>
          <PlayGlyph playing={isPlaying || isLoading} />
        </button>

        <div className="p-now">
          <div className="pt" aria-live="polite">
            {readout.title}
            {readout.suffix && <span className="pt__w"> {readout.suffix}</span>}
          </div>
          {error ? (
            <div className="p-err" role="status">
              <span className="p-err__blk" aria-hidden="true" />
              <span className="mono p-err__msg">{error}</span>
            </div>
          ) : (
            <div className={readout.onAir ? 'mono p-line' : 'mono p-line p-dim'}>{readout.meta}</div>
          )}
        </div>

        <div className="p-meter">
          <LevelMeter running={readout.running} quiet={readout.quiet} />
          <div className={readout.onAir ? 'mono-13' : 'mono-13 p-dim'}>{readout.word}</div>
        </div>

        <div className="p-tail">
          <VolumeControl />
        </div>

        <ChatLauncher />
      </div>
    </>
  );
}
